import { fit } from 'fit-to-bounds'
import React from 'react'
import styled, { css } from 'styled-components'
import ModuleData, { isPlacable } from '../../../types/ModuleData'
import { useSelector } from '../../store/hooks'
import { useCurrentApplication } from '../../store/hooks/uiHooks'

export type PreviewModuleProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  module?: ModuleData
  scale?: number
}

export const useDefaultModuleScale = (): { applicationScale: number; moduleScale: number } => {
  const dimensions = useSelector((state) => state.ui.screenSize)
  const application = useCurrentApplication()
  if (!application) {
    return { applicationScale: 0, moduleScale: 0 }
  }
  const { scale: applicationScale } = fit('cover', dimensions, application.backgroundImage)
  const lastIndex = application.luminaries.length - 1
  const stele = application.luminaries[lastIndex]
  if (!stele) {
    return { applicationScale, moduleScale: 0 }
  }
  return { applicationScale, moduleScale: applicationScale * stele.scale }
}

export const getActualModuleOffset = (
  systemWidth: number,
  module: ModuleData | undefined,
  scale: number
): {
  left: number
  right: number
} => {
  if (!isPlacable(module)) {
    return {
      left: 0,
      right: 0
    }
  }
  const { center, width } = module
  const moduleCenter = center ?? width / 2
  const left = systemWidth / 2 - moduleCenter
  const right = systemWidth - left - width

  return { left: Math.round(scale * left), right: Math.round(scale * right) }
}

const PreviewModuleWrap = styled.div<{
  systemWidth: number
  module: ModuleData
  scale: number
  height: number
}>`
  ${({ systemWidth, module, scale }) => {
    if (!isPlacable(module)) {
      return css`
        width: 50px;
        height: 50px;
        background-color: #fff;
        border-radius: 100%;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
        padding: 15%;
        margin: 7px;
      `
    }
    const { left, right } = getActualModuleOffset(systemWidth, module, scale)
    return css`
      width: ${Math.round(scale * systemWidth)}px;
      height: ${Math.floor(scale * module.height)}px;
      padding-left: ${left}px;
      padding-right: ${right}px;
    `
  }};
`

const PreviewModuleInner = styled.div<{
  module: ModuleData
}>`
  width: 100%;
  height: 100%;
  background-image: url(${({ module }) => JSON.stringify(module?.preview.url)});
  background-size: 100% auto;
  background-position: center
    ${({ module }) =>
      module?.type === 'LightingHead' ? 'top' : module?.type === 'SupportingColumn' ? 'bottom' : 'center'};
`

const OHidden = styled.div`
  overflow: hidden;
`

const PreviewModule = React.forwardRef<HTMLDivElement, PreviewModuleProps>(function PreviewModule(
  { module, scale, ...props },
  ref
) {
  const systemWidth = useSelector((state) => state.data.system.width)

  const { moduleScale } = useDefaultModuleScale()

  if (!module) {
    return <div ref={ref} {...props}></div>
  }

  return (
    <OHidden>
      <PreviewModuleWrap
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        {...props}
        systemWidth={systemWidth}
        module={module}
        scale={scale ?? moduleScale}
      >
        <PreviewModuleInner module={module} />
      </PreviewModuleWrap>
    </OHidden>
  )
})

export default PreviewModule
